import { useContext, useEffect, useState } from "react";
import ContentCreator from "../../../components/ContentCreator/ContentCreator";
import axios from "axios";
import showNotification from "../../../utils/showNotification";
import AuthContext from "../../../contexts/AuthContext";
import SalesLeadsForm from "../SalesLeadsForm/SalesLeadsForm";
import { ERROR_HANDLER } from "../../../utils/error-handler";
import moment from "moment/moment";
import { LEADS_OUTCOME } from "../../../utils/leads-constant";
import { ActionIcon, Badge, Button, Divider, Flex, Grid, Group, Loader, Menu, Modal, Pagination, Paper, Select, Stack, Table, Text, TextInput } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../contexts/AppContext";
import { IconAt, IconChevronRight, IconCopy, IconEdit, IconPhone, IconSearch, IconUser, IconX } from "@tabler/icons-react";
import { handleCopy } from "../../../utils/handleCopy";
import { useDebouncedValue } from "@mantine/hooks";
import { flattenObject } from "../../../utils/flattenObject";
import { DatePickerInput } from "@mantine/dates";



const mapLastContactStatus = (outcome) => {
    const found = LEADS_OUTCOME.find(o => o.value === outcome);
    const text = found?.label || "Sin info";

    return text
}

const validation = {}


export default function SalesLeadsContainer() {
    const navigate = useNavigate()
    const { authToken } = useContext(AuthContext)
    const { academicUnits, staffUsers } = useContext(AppContext)

    const [displayForm, setDisplayForm] = useState(false);
    const [updateData, setUpdateData] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isFetchingLoading, setIsFetchingLoading] = useState(false)
    const [initialValues, setInitialValues] = useState(null)
    const [leadsData, setLeadsData] = useState([])
    const [genericModal, setGenericModal] = useState(null)
    // Estados de Paginación
    const [activePage, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState("20");

    // Estados de Filtros
    const [filters, setFilters] = useState({
        name: '',
        email: '',
        phone: '',
        seller: null,
        unitId: null,
        outcome: ''
    });

    // Debounce de los filtros de texto (espera 500ms tras dejar de escribir)
    const [debouncedFilters] = useDebouncedValue(filters, 1250);
    const [dateRange, setDateRange] = useState([null, null]);

    const downloadCSV = async (_res) => {
        const url = window.URL.createObjectURL(new Blob([_res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "export.csv");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    };

    const handleExport = async () => {
        // 1. Usamos directamente leadsData (el estado donde guardas la respuesta del back)
        // O si usas TanStack Table: table.getRowModel().rows.map(row => row.original)
        const dataToExport = leadsData;

        if (!dataToExport || dataToExport.length === 0) {
            showNotification({
                color: "red",
                title: "Error al exportar",
                message: "No hay datos en la tabla para exportar."
            });
            return;
        }

        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
            responseType: 'blob'
        };

        // 2. Al ser datos ya procesados por el back, vienen mayormente planos.
        // Aun así, aplicamos tu lógica de normalización por seguridad.
        const allColumns = new Set();
        const processedData = dataToExport.map((row) => {
            // Si el objeto ya es plano, flattenObject simplemente lo devolverá igual
            // pero asegura que si hay algún objeto anidado residual se aplane.
            const flattened = flattenObject(row);
            Object.keys(flattened).forEach(key => allColumns.add(key));
            return flattened;
        });

        const columns = Array.from(allColumns);

        // 3. Aseguramos consistencia de columnas (evita que el CSV se rompa si falta un campo)
        const normalizedData = processedData.map(row => {
            const normalizedRow = {};
            columns.forEach(col => {
                normalizedRow[col] = row[col] !== undefined ? row[col] : '';
            });
            return normalizedRow;
        });

        const jsonData = {
            data: normalizedData,
            columns: columns,
        };

        try {
            // 4. Enviamos al endpoint de utilidad que convierte JSON a CSV
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_SERVER}admin/exportCSV`,
                jsonData,
                config
            );

            await downloadCSV(response);

            showNotification({
                color: "green",
                title: "¡Excelente!",
                message: `Se han exportado ${normalizedData.length} filas correctamente.`,
            });
        } catch (error) {
            showNotification({
                color: "red",
                title: "Error al exportar CSV",
                message: error.message
            });
        }
    };

    const renderColor = (_outcome) => {
        if (!_outcome || typeof _outcome !== 'string') return 'transparent'
        const current_outcome = _outcome.toUpperCase()
        if (current_outcome === "PENDING") return "var(--color-yellow)"
        if (current_outcome === "SUCCESS") return "var(--color-green)"
        if (current_outcome === "FAILED") return "var(--color-red)"
        if (current_outcome === "AUTO-FAILED") return "var(--color-badge-red)"
        if (current_outcome === "IN-PROGRESS") return "var(--color-badge-yellow)"
        if (current_outcome === "WAITING-FOR-PAYMENT") return "var(--color-badge-yellow)"
        return 'transparent'
    }

    useEffect(() => {
        const getData = async () => {
            const axiosConfig = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`
                }
            };
            setIsFetchingLoading(true)
            const URL = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/leads`

            const [start, end] = dateRange;

            try {
                const params = new URLSearchParams({
                    page: activePage,
                    limit: itemsPerPage,
                    name: debouncedFilters.name,
                    email: debouncedFilters.email,
                    phone: debouncedFilters.phone?.trim().replaceAll(' ', '') || '',
                    ...(debouncedFilters.unitId && { unitId: debouncedFilters.unitId }),
                    ...(debouncedFilters.seller && { seller: debouncedFilters.seller }),
                    ...(debouncedFilters.outcome && { outcome: debouncedFilters.outcome }),
                    ...(start && { fromDate: start.toISOString() }),
                    ...(end && { toDate: end.toISOString() })
                });
                const { data } = await axios.get(`${URL}?${params.toString()}`, axiosConfig)
                setTotalPages(data.totalPages);
                setTotalRecords(data.totalRecords);
                setLeadsData(data.data || []);
            } catch (error) {
                showNotification({
                    title: "Ha ocurrido un error",
                    message: error.message || "No se han podido cargar los prospectos"
                })
                console.log(error)
            } finally {
                setIsFetchingLoading(false)
            }
        }
        getData()
    }, [updateData, activePage, debouncedFilters, itemsPerPage, dateRange])

    const resetFilters = () => {
        setFilters({ name: '', email: '', phone: '', seller: '', unitId: '' });
        setDateRange([null, null]);
        setPage(1);
    };

    const handleEditLead = (rowData) => {
        if (rowData) {
            setInitialValues({
                ...rowData,
            });
            setDisplayForm(true);
        }
    }

    const createLead = async (values, config) => {



        const jsonData = {
            ...values,
            sendEmail: false,
            source: values.leadSource,
        };
        return axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}info/createLead`,
            jsonData,
            config
        );
    };

    const editLead = async (values, config, _uploadToS3) => {
        const jsonData = {
            ...values,
            contactAttempts: values.contactAttempts?.map(item => ({ ...item, seller_id: item?.seller_id || null }))
        };

        return axios.put(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/leads/${values._id}`,
            jsonData,
            config
        );
    };

    const handleSubmit = async (values) => {
        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
        };

        try {
            setLoading(true);
            let notificationMessage = ''

            if (initialValues) {
                await editLead(values, config);
                notificationMessage = `El prospecto ha sido editado con éxito.`
            } else {
                await createLead(values, config);
                notificationMessage = `El prospecto ha sido creado con éxito.`
            }

            showNotification({
                color: "green",
                status: "success",
                title: "¡Excelente! Operación exitosa.",
                message: notificationMessage,
            });



            if (values.createSale) {
                navigate('/admin/ventas/pagos-ingresados', {
                    state: {
                        name: values.name?.trim(),
                        lastname: values.lastname?.trim(),
                        email: values.email?.trim(),
                        phone: values.phone?.trim(),
                        dni: "",
                        currency: "USD",
                        paymentMethod: "transfer",
                        academicUnitId: values.createSale,
                        source: "ACADEMIC_UNIT",
                        sellerId: values.current_seller_id,
                        alreadyUser: false,
                        amountDeposited: {
                            amount: 0,
                            currency: "USD"
                        }
                    }
                })
            }

            setUpdateData(!updateData);
            setDisplayForm(false);
            setInitialValues(null);

        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo crear / editar la inscripción. Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
                    }`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="salesBulkMessages__container">
            {displayForm && (
                <ContentCreator
                    onBackButtonText='Volver sin guardar'
                    title={initialValues ? "Editar prospecto" : "Crear prospecto"}
                    submitFunc={(values) => handleSubmit(values)}
                    onCancel={() => {
                        setDisplayForm(false);
                        setInitialValues(null);
                    }}
                    validation={validation}
                    initialValues={initialValues ?? {
                        contactAttempts: [],
                        notes: []
                    }}
                    renderPreview={(_form) => (
                        <></>
                    )}
                    renderForm={(_form) => (
                        <SalesLeadsForm form={_form.form} loading={loading} />
                    )}
                />
            )}

            {!displayForm && <Stack w='100%' spacing="md" pb={100}>
                <Flex justify='end' >
                    <Button color='var(--color-primary)' variant='light' onClick={handleExport}>
                        Exportar CSV
                    </Button>
                    <Button
                        onClick={() => {
                            setDisplayForm(!displayForm);
                            if (displayForm) setInitialValues(null)
                        }}
                        w={'max-content'}
                        color='var(--color-tertiary)'
                        className='TableHeader-create-button'
                    >
                        <span>Crear prospecto</span>
                    </Button>
                </Flex>
                <Paper withBorder p="md" shadow="xs" radius="md">
                    <Grid align="flex-end">
                        <Grid.Col span={12} >
                            <Group grow align="flex-end">
                                <Select
                                    label="Cantidad de filas para mostrar"
                                    data={["10", "20", "50", "100"]}
                                    maw='300px'
                                    value={itemsPerPage}
                                    onChange={(val) => { setItemsPerPage(val); setPage(1); }}
                                />
                                <ActionIcon
                                    color="red"
                                    variant="light"
                                    size="lg"
                                    onClick={resetFilters}
                                    title="Limpiar filtros"
                                >
                                    <IconX size={20} />
                                    <span style={{ marginLeft: 8, fontSize: 'var(--fs-small)' }}>Borrar filtros</span>
                                </ActionIcon>
                            </Group>
                        </Grid.Col>
                        <Grid.Col span={12}>
                            <Divider variant="dashed" />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                            <TextInput
                                label="Nombre"
                                placeholder="Ej: Juan Pérez"
                                icon={<IconUser size={16} />}
                                value={filters.name}
                                onChange={(e) => { setFilters({ ...filters, name: e.target.value }); setPage(1); }}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                            <TextInput
                                label="Email"
                                placeholder="email@ejemplo.com"
                                icon={<IconAt size={16} />}
                                value={filters.email}
                                onChange={(e) => { setFilters({ ...filters, email: e.target.value }); setPage(1); }}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                            <TextInput
                                label="Teléfono"
                                placeholder="Buscar número..."
                                icon={<IconPhone size={16} />}
                                value={filters.phone}
                                onChange={(e) => { setFilters({ ...filters, phone: e.target.value }); setPage(1); }}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
                            <Select
                                label="Vendedor"
                                placeholder="Seleccionar..."
                                clearable
                                data={staffUsers}
                                value={filters.seller}
                                onChange={(val) => { setFilters({ ...filters, seller: val }); setPage(1); }}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <Select
                                label="Unidad academica"
                                placeholder="Seleccionar..."
                                clearable
                                data={academicUnits}
                                value={filters.unitId}
                                onChange={(val) => { setFilters({ ...filters, unitId: val }); setPage(1); }}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <DatePickerInput
                                label="Rango de fecha a partir del ultimo contacto"
                                placeholder="Desde - Hasta"
                                type="range"
                                clearable
                                value={dateRange}
                                onChange={(val) => { setDateRange(val); setPage(1); }}
                                icon={<IconSearch size={16} />} // O IconCalendar
                            />
                        </Grid.Col>
                        <Grid.Col>
                            <Select
                                label="Estado"
                                placeholder="Seleccionar..."
                                clearable
                                data={LEADS_OUTCOME}
                                value={filters.outcome}
                                onChange={(val) => { setFilters({ ...filters, outcome: val }); setPage(1); }}
                            />
                        </Grid.Col>
                    </Grid>
                </Paper>




                <Paper withBorder radius="md" style={{ overflow: 'auto', position: 'relative' }}>
                    {isFetchingLoading && (
                        <Flex
                            justify="center" align="center"
                            style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 2 }}
                        >
                            <Loader variant="dots" />
                        </Flex>
                    )}


                    <Table verticalSpacing="sm" highlightOnHover>
                        <Table.Thead style={{ backgroundColor: 'var(--color-primary-dark)', color: '#fff' }}>
                            <Table.Tr >
                                <Table.Th>Último Contacto</Table.Th>
                                <Table.Th>Estado</Table.Th>
                                <Table.Th>Prospecto</Table.Th>
                                <Table.Th>
                                    Contacto
                                    <Menu>
                                        <Menu.Target>
                                            <Button type="button" variant="transparent"><IconCopy /></Button>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Item leftSection={<IconAt size={14} />} onClick={() => {
                                                const filteredEmails = leadsData
                                                    .map(r => r.email)
                                                    .filter(Boolean)
                                                    .filter(_emails => _emails !== 'N/A')
                                                    .join(", ");

                                                handleCopy(filteredEmails, "Se han copiado todos los emails");
                                            }}>
                                                Copiar emails visibles
                                            </Menu.Item>
                                            <Menu.Item leftSection={<IconPhone size={14} />}
                                                onClick={() => {
                                                    const filteredPhones = leadsData
                                                        .map(r => r.phone)
                                                        .filter(Boolean)
                                                        .join(", ");

                                                    handleCopy(filteredPhones, "Se han copiado todos los telefonos");
                                                }}
                                            >
                                                Copiar telefonos visibles
                                            </Menu.Item>
                                        </Menu.Dropdown>
                                    </Menu>
                                </Table.Th>
                                <Table.Th>Vendedor</Table.Th>
                                <Table.Th>Notas</Table.Th>
                                <Table.Th>Acciones</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody >
                            {totalRecords > 0 && leadsData.map((lead) => (
                                <Table.Tr key={lead._id}>
                                    <Table.Td>
                                        <Text size="sm" weight={500}>
                                            {lead.lastContactDate ? moment(lead.lastContactDate).format("DD/MM/YYYY") : "N/A"}
                                        </Text>
                                        <Text size="xs" color="dimmed">
                                            {lead.daysSinceLastContact !== null ? `Hace ${lead.daysSinceLastContact} días` : ""}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge color={renderColor(lead.lastOutcome)}
                                        >
                                            {mapLastContactStatus(lead.lastOutcome)}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Text size="sm" weight={500}>{lead.full_name}</Text>
                                        <Text size="xs" color="dimmed">{lead.username}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Stack spacing={0}>
                                            {lead.email && <Text size="xs">{lead.email} <IconCopy style={{ cursor: 'pointer' }} size={16} onClick={() => {
                                                handleCopy(lead.email, "Se ha copiado el email");
                                            }} /></Text>}
                                            {lead.phone && <Text size="xs" color="dimmed"

                                            >{lead.phone}
                                                <IconCopy style={{ cursor: 'pointer' }} size={16} onClick={() => {
                                                    handleCopy(lead.phone, "Se ha copiado el telefono")
                                                }} />
                                            </Text>}
                                        </Stack>
                                    </Table.Td>
                                    <Table.Td><Badge color="gray" variant="outline">{lead.sellerName}</Badge></Table.Td>
                                    <Table.Td>
                                        <Text size="xs" color="dimmed" lineClamp={1} style={{ cursor: 'pointer' }}

                                            onClick={() => {
                                                if ([...lead.formattedNotes]?.length === 0) return

                                                setGenericModal({ data: [...lead.formattedNotes.split(',')], title: "Notas" })
                                            }}

                                        >
                                            {lead.formattedNotes || "Sin notas"}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <IconEdit style={{ cursor: 'pointer' }} onClick={() => handleEditLead(lead)} />
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Paper>

                <Group position="apart">
                    <Text size="sm" color="dimmed">
                        Total: <b>{totalRecords}</b> registros encontrados
                    </Text>
                    <Pagination total={totalPages} page={activePage} onChange={setPage} radius="md" />
                </Group>
            </Stack>}
            {genericModal && <Modal
                opened={genericModal}
                onClose={() => setGenericModal(null)}>
                {genericModal?.title && <h1 style={{ fontSize: "24px" }}>{genericModal?.title}</h1>}
                <Stack gap={4}>
                    {genericModal?.data?.map(item =>
                        <Flex key={item}>
                            <IconChevronRight />
                            <p style={{ fontSize: "16px" }} >{item}</p>
                        </Flex>
                    )}
                </Stack>
                <Button mt={16} type='button' onClick={() => setGenericModal(null)}>
                    Cerrar
                </Button>
            </Modal>}
        </div>
    );
}